import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, type DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import dayjs, { type Dayjs } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useEffect, useRef, useState } from 'react';
import { MdOutlineCalendarMonth } from 'react-icons/md';

dayjs.extend(relativeTime);

interface Props {
	value?: Dayjs | null;
	onChange?: (value: Dayjs | null) => void;
	isInvalid?: boolean;
	errorMessage?: string;
	disabled?: boolean;
	showRelativeDate?: boolean;
	onClickCallback?: () => void;
	yearAndMonthOnly?: boolean;
	bgColor?: string;
	selectedOption?: string;
	format?: string;
	onClose?: (date: Dayjs | null) => void;
	restProps?: DatePickerProps<Dayjs>;
}

const DateSelect = ({
	value,
	onChange,
	isInvalid,
	errorMessage,
	disabled,
	onClickCallback,
	bgColor,
	onClose,
	format = 'DD / MM / YYYY',
	restProps,
}: Props) => {
	const [date, setDate] = useState<Dayjs | null>(value || null);
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const handleDateChange = (date: Dayjs | null) => {
		setDate(date);
		onChange?.(date);
	};

	useEffect(() => {
		if (value) {
			setDate(value);
			!onClose && setIsOpen(false);
		}
	}, [value]);

	return (
		<div className="relative">
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<button
					className="flex bg-white w-full justify-between h-[3rem] rounded-[8px] items-center outline-none px-[0.75rem] border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 cursor-pointer"
					onClick={() => {
						if (!disabled) {
							setIsOpen(!isOpen);
							onClickCallback?.();
						}
					}}
				>
					<p className="text-sm">{date ? date?.format(format) : format ? format : 'Select Date'}</p>
					<div>
						<MdOutlineCalendarMonth />
					</div>
				</button>
				<div className="absolute w-full h-full top-0 left-0 invisible">
					<DatePicker
						inputRef={inputRef}
						open={isOpen}
						value={date}
						onClose={() => {
							if (onClose) {
								onClose(date);
							} else {
								setIsOpen(false);
							}
						}}
						onChange={date => handleDateChange(date as Dayjs)}
						{...restProps}
					/>
				</div>
			</LocalizationProvider>
			{isInvalid && <p className="text-red-500 mt-[0.5rem] text-sm">{errorMessage}</p>}
		</div>
	);
};

export default DateSelect;
